/**
 * FeeEditDialog Component
 * 
 * Specialized dialog for editing transfer fee or expense fee transactions.
 * Only allows editing fee amount and fee account.
 * Shows related transfer or expense information (read-only) with transaction ID and copy.
 */

import React, { FormEvent, useState, useCallback, useRef, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Autocomplete,
  Box,
  Typography,
  Stack,
  IconButton,
  InputAdornment,
  Paper,
  Divider,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { Account, Currency } from "../../../types";
import { TransactionWithDate } from "../../../utils/transactionProcessing";
import { formatCurrency, formatDateLocal, formatTimeLocal, getTransactionFriendlyId, copyToClipboard } from "../../../utils/transactionUtils";
import { getCurrencyDecimalPlaces } from "../../../utils/currencyDefaults";
import CopyFeedbackTooltip from "../../../components/CopyFeedbackTooltip";

export interface FeeEditFormData {
  feeAmount: string;
  feeAccountId: string;
}

interface FeeEditDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (e: FormEvent<HTMLFormElement>) => Promise<void>;
  
  // Form data
  formData: FeeEditFormData;
  setFormData: React.Dispatch<React.SetStateAction<FeeEditFormData>>;
  
  // Fee transaction being edited
  feeTransaction: TransactionWithDate;
  
  // Related transfer or expense transaction (read-only info)
  transferTransaction: TransactionWithDate | null;
  expenseTransaction?: TransactionWithDate | null;
  
  // For showing transaction ID on related cards
  transactionIdPrefix?: string;
  
  // Data
  accounts: Account[];
  currencies: Currency[];
  defaultCurrency: string;
  
  // State
  isSubmitting?: boolean;
  
  // Validation
  isValid?: boolean;
  /** Disable Update button when user has not made any changes to the fee (default true when not provided) */
  hasFeeChanges?: boolean;
}

export default function FeeEditDialog({
  open,
  onClose,
  onSubmit,
  formData,
  setFormData,
  feeTransaction,
  transferTransaction,
  expenseTransaction = null,
  transactionIdPrefix = "",
  accounts,
  currencies,
  defaultCurrency,
  isSubmitting = false,
  isValid = true,
  hasFeeChanges = true,
}: FeeEditDialogProps): React.ReactElement {
  const [lastCopiedKey, setLastCopiedKey] = useState<string | null>(null);
  const copyFeedbackTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const showCopiedFeedback = useCallback((key: string) => {
    if (copyFeedbackTimeoutRef.current) clearTimeout(copyFeedbackTimeoutRef.current);
    setLastCopiedKey(key);
    copyFeedbackTimeoutRef.current = setTimeout(() => {
      setLastCopiedKey(null);
      copyFeedbackTimeoutRef.current = null;
    }, 2000);
  }, []);
  useEffect(() => () => { if (copyFeedbackTimeoutRef.current) clearTimeout(copyFeedbackTimeoutRef.current); }, []);

  const feeAccount = formData.feeAccountId
    ? accounts.find((acc) => acc.id === formData.feeAccountId && !acc.isArchived)
    : null;

  // Get transfer accounts
  const fromAccount = transferTransaction?.fromAccountId
    ? accounts.find((acc) => acc.id === transferTransaction.fromAccountId && !acc.isArchived)
    : null;
  const toAccount = transferTransaction?.toAccountId
    ? accounts.find((acc) => acc.id === transferTransaction.toAccountId && !acc.isArchived)
    : null;

  // Format transfer date and time
  const transferDate = transferTransaction?.date
    ? formatDateLocal(transferTransaction.date)
    : "";
  const transferTime = transferTransaction?.date
    ? formatTimeLocal(transferTransaction.date)
    : "";
  const hasTransferTime = transferTime && transferTime !== "00:00";

  const expenseDate = expenseTransaction?.date
    ? formatDateLocal(expenseTransaction.date)
    : "";

  const feeIdToShow = getTransactionFriendlyId(transactionIdPrefix, feeTransaction.friendlyIdSequence) ?? feeTransaction.id;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, flexWrap: "wrap" }}>
          <Stack direction="row" spacing={2} alignItems="baseline" sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="h6">Edit Fee</Typography>
            {feeIdToShow && (
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "baseline",
                  ml: 1,
                  "& .copy-id-btn": { opacity: 0, transition: "opacity 0.15s ease" },
                  "&:hover .copy-id-btn, &:focus-within .copy-id-btn": { opacity: 1 },
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  ID: {feeIdToShow}
                </Typography>
                <CopyFeedbackTooltip copyLabel="Copy ID" feedbackKey={`fee-dialog-title-id-${feeTransaction.id}`} lastCopiedKey={lastCopiedKey} isMobile={false}>
                  <IconButton
                    className="copy-id-btn"
                    size="small"
                    onClick={() => copyToClipboard(feeIdToShow).then((ok) => ok && showCopiedFeedback(`fee-dialog-title-id-${feeTransaction.id}`))}
                    sx={{ p: 0.25, ml: 0.25 }}
                    aria-label="Copy transaction ID"
                  >
                    <ContentCopyIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                </CopyFeedbackTooltip>
              </Box>
            )}
          </Stack>
          <IconButton size="small" onClick={onClose} sx={{ flexShrink: 0 }}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <form onSubmit={onSubmit} id="fee-edit-form">
          <Stack spacing={3} sx={{ mt: 1 }}>
            {/* Related Transfer Information (Read-only) */}
            {transferTransaction && (
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  bgcolor: "action.hover",
                  borderRadius: 1,
                  border: 1,
                  borderColor: "divider",
                }}
              >
                <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 0.5 }}>
                  Related Transfer
                </Typography>
                <Stack spacing={1}>
                  {/* From Account */}
                  {fromAccount && (
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        From Account
                      </Typography>
                      <Typography variant="body2">
                        {fromAccount.accountName} ({formatCurrency(fromAccount.amount || 0, fromAccount.currency, getCurrencyDecimalPlaces(fromAccount.currency, currencies, defaultCurrency))} {fromAccount.currency})
                      </Typography>
                    </Box>
                  )}

                  {/* To Account */}
                  {toAccount && (
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        To Account
                      </Typography>
                      <Typography variant="body2">
                        {toAccount.accountName} ({formatCurrency(toAccount.amount || 0, toAccount.currency, getCurrencyDecimalPlaces(toAccount.currency, currencies, defaultCurrency))} {toAccount.currency})
                      </Typography>
                    </Box>
                  )}

                  {/* Transfer Amount */}
                  {transferTransaction.amount !== undefined && (
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Transfer Amount
                      </Typography>
                      <Typography variant="body2">
                        {formatCurrency(transferTransaction.amount, transferTransaction.currency || defaultCurrency)}
                      </Typography>
                    </Box>
                  )}

                  {/* Date */}
                  {transferDate && (
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Date
                      </Typography>
                      <Typography variant="body2">{transferDate}</Typography>
                    </Box>
                  )}

                  {/* Time (if exists) */}
                  {hasTransferTime && (
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Time
                      </Typography>
                      <Typography variant="body2">{transferTime}</Typography>
                    </Box>
                  )}

                  {/* Transaction ID with copy */}
                  {(getTransactionFriendlyId(transactionIdPrefix, transferTransaction.friendlyIdSequence) || transferTransaction.id) && (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                      <Typography variant="caption" color="text.secondary">
                        ID
                      </Typography>
                      <Typography variant="body2" sx={{ fontSize: "0.8125rem", minWidth: 0 }}>
                        {getTransactionFriendlyId(transactionIdPrefix, transferTransaction.friendlyIdSequence) ?? transferTransaction.id}
                      </Typography>
                      <CopyFeedbackTooltip copyLabel="Copy ID" feedbackKey={`fee-dialog-transfer-id-${transferTransaction.id}`} lastCopiedKey={lastCopiedKey} isMobile={false}>
                        <IconButton
                          size="small"
                          onClick={() => {
                            const id = getTransactionFriendlyId(transactionIdPrefix, transferTransaction.friendlyIdSequence) ?? transferTransaction.id;
                            copyToClipboard(id).then((ok) => ok && showCopiedFeedback(`fee-dialog-transfer-id-${transferTransaction.id}`));
                          }}
                          sx={{ p: 0.25 }}
                          aria-label="Copy transaction ID"
                        >
                          <ContentCopyIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                      </CopyFeedbackTooltip>
                    </Box>
                  )}
                </Stack>
              </Paper>
            )}

            {/* Related Expense (read-only) - for expense fee */}
            {expenseTransaction && (
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  bgcolor: "action.hover",
                  borderRadius: 1,
                  border: 1,
                  borderColor: "divider",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, flexWrap: "wrap", mb: 0.5 }}>
                  <Typography variant="subtitle2" fontWeight="bold">
                    Related Expense
                  </Typography>
                  {(getTransactionFriendlyId(transactionIdPrefix, expenseTransaction.friendlyIdSequence) || expenseTransaction.id) && (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: "0.8125rem" }}>
                        ID: {getTransactionFriendlyId(transactionIdPrefix, expenseTransaction.friendlyIdSequence) ?? expenseTransaction.id}
                      </Typography>
                      <CopyFeedbackTooltip copyLabel="Copy ID" feedbackKey={`fee-dialog-expense-id-${expenseTransaction.id}`} lastCopiedKey={lastCopiedKey} isMobile={false}>
                        <IconButton
                          size="small"
                          onClick={() => {
                            const id = getTransactionFriendlyId(transactionIdPrefix, expenseTransaction.friendlyIdSequence) ?? expenseTransaction.id;
                            copyToClipboard(id).then((ok) => ok && showCopiedFeedback(`fee-dialog-expense-id-${expenseTransaction.id}`));
                          }}
                          sx={{ p: 0.25 }}
                          aria-label="Copy transaction ID"
                        >
                          <ContentCopyIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                      </CopyFeedbackTooltip>
                    </Box>
                  )}
                </Box>
                <Stack spacing={1}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Category
                    </Typography>
                    <Typography variant="body2">{expenseTransaction.category ?? expenseTransaction.title ?? ""}</Typography>
                  </Box>
                  {expenseTransaction.amount !== undefined && (
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Amount
                      </Typography>
                      <Typography variant="body2">
                        {formatCurrency(expenseTransaction.amount, expenseTransaction.currency || defaultCurrency, getCurrencyDecimalPlaces(expenseTransaction.currency || defaultCurrency, currencies, defaultCurrency))}
                      </Typography>
                    </Box>
                  )}
                  {expenseDate && (
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Date
                      </Typography>
                      <Typography variant="body2">{expenseDate}</Typography>
                    </Box>
                  )}
                </Stack>
              </Paper>
            )}

            <Divider />

            {/* Editable Fields */}
            <Typography variant="subtitle2" fontWeight="bold">
              Fee Details
            </Typography>

            {/* Fee Amount */}
            <TextField
              required
              label="Fee Amount"
              type="number"
              inputProps={{ step: "any", min: "0" }}
              value={formData.feeAmount}
              onChange={(e) => {
                const value = e.target.value;
                // Only allow digits and a single decimal point
                if (value === "" || /^\d*\.?\d*$/.test(value)) {
                  setFormData({ ...formData, feeAmount: value });
                }
              }}
              onPaste={(e) => {
                e.preventDefault();
                const pastedText = e.clipboardData.getData('text');
                const sanitized = pastedText.replace(/[^\d.]/g, '');
                const parts = sanitized.split('.');
                const cleaned = parts.length > 0 
                  ? parts[0] + (parts.length > 1 ? '.' + parts.slice(1).join('') : '')
                  : '';
                if (cleaned === "" || /^\d*\.?\d*$/.test(cleaned)) {
                  setFormData({ ...formData, feeAmount: cleaned });
                }
              }}
              fullWidth
              placeholder="0.00"
              InputProps={{
                endAdornment: feeAccount ? (
                  <InputAdornment position="end">
                    <Typography variant="body2" color="text.secondary">
                      {feeAccount.currency}
                    </Typography>
                  </InputAdornment>
                ) : undefined,
                sx: {
                  "& input[type=number]": {
                    MozAppearance: "textfield",
                  },
                  "& input[type=number]::-webkit-outer-spin-button": {
                    WebkitAppearance: "none",
                    margin: 0,
                  },
                  "& input[type=number]::-webkit-inner-spin-button": {
                    WebkitAppearance: "none",
                    margin: 0,
                  },
                },
              }}
            />

            {/* Fee Account */}
            <Autocomplete
              fullWidth
              options={accounts
                .filter((acc) => !acc.isArchived)
                .sort((a, b) => {
                  if (a.group !== b.group) {
                    return a.group.localeCompare(b.group);
                  }
                  return a.accountName.localeCompare(b.accountName);
                })}
              value={feeAccount || null}
              onChange={(_, newValue: Account | null) => {
                setFormData({
                  ...formData,
                  feeAccountId: newValue?.id || "",
                });
              }}
              groupBy={(option: Account) => option.group}
              getOptionLabel={(option: Account) =>
                `${option.accountName} (${formatCurrency(option.amount || 0, option.currency, getCurrencyDecimalPlaces(option.currency, currencies, defaultCurrency))} ${option.currency})`
              }
              filterOptions={(options: Account[], { inputValue }) => {
                const searchValue = inputValue.toLowerCase();
                return options.filter(
                  (option) =>
                    option.accountName.toLowerCase().includes(searchValue) ||
                    option.group.toLowerCase().includes(searchValue)
                );
              }}
              renderOption={(props, option: Account) => (
                <Box component="li" {...props} key={option.id} sx={{ pl: 3 }}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      width: "100%",
                    }}
                  >
                    <Typography variant="body2">{option.accountName}</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
                      {formatCurrency(option.amount || 0, option.currency, getCurrencyDecimalPlaces(option.currency, currencies, defaultCurrency))} ({option.currency})
                    </Typography>
                  </Box>
                </Box>
              )}
              renderGroup={(params) => (
                <li key={params.key}>
                  <Box
                    sx={{
                      position: "sticky",
                      top: 0,
                      bgcolor: "background.paper",
                      zIndex: 1,
                      py: 0.5,
                      px: 1,
                    }}
                  >
                    <Typography variant="subtitle2" fontWeight="bold" color="text.primary">
                      {params.group}
                    </Typography>
                  </Box>
                  <Box component="ul" sx={{ p: 0 }}>
                    {params.children}
                  </Box>
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Fee Account"
                  required
                />
              )}
              isOptionEqualToValue={(option: Account, value: Account) => option.id === value.id}
            />
          </Stack>
        </form>
      </DialogContent>
      <DialogActions sx={{ px: 2, pb: 2, pt: 1, gap: 1, display: "flex" }}>
        <Button onClick={onClose} variant="outlined" sx={{ flex: 1, py: 1.5 }}>
          Cancel
        </Button>
        <Button
          type="submit"
          form="fee-edit-form"
          variant="contained"
          sx={{ flex: 1, py: 1.5 }}
          disabled={isSubmitting || !isValid || !hasFeeChanges}
        >
          {isSubmitting ? "Updating..." : "Update Fee"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
